import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { statusToneClasses } from '@/lib/statusTone';
import { cn } from '@/lib/utils';

export function FormSection({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-3', className)}>
      {title ? (
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
              {description ? <p className="text-xs text-text-secondary">{description}</p> : null}
            </div>
            {actions ? <div className="shrink-0">{actions}</div> : null}
          </div>
          <div className="h-px w-full bg-[var(--border-subtle)]" />
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2', className)}>
      {children}
    </div>
  );
}

export function FormField({
  label,
  htmlFor,
  required,
  description,
  error,
  colSpan = 1,
  className,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  required?: boolean;
  description?: string;
  error?: string;
  colSpan?: 1 | 2;
  className?: string;
  children: ReactNode;
}) {
  const errorTone = statusToneClasses('danger');
  return (
    <div className={cn('space-y-2', colSpan === 2 ? 'md:col-span-2' : null, className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-text-primary">
        {label}
        {required ? <span className="ml-1 text-destructive">*</span> : null}
      </label>
      <div className="space-y-2">
        {children}
        {error ? (
          <p className={cn('text-sm', errorTone.text)} role="alert">
            {error}
          </p>
        ) : null}
        {description ? <p className="text-xs text-text-secondary">{description}</p> : null}
      </div>
    </div>
  );
}

export function CheckboxRow({
  id,
  label,
  description,
  required,
  className,
  children,
}: {
  id?: string;
  label: ReactNode;
  description?: string;
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex items-center gap-3 py-1', className)}>
      {children}
      <div className="space-y-2">
        <label htmlFor={id} className="text-sm font-medium text-text-primary">
          {label}
          {required ? <span className="ml-1 text-destructive">*</span> : null}
        </label>
        {description ? <p className="text-xs text-text-secondary">{description}</p> : null}
      </div>
    </div>
  );
}

export function FormErrorBanner({ message, className }: { message: string; className?: string }) {
  const errorTone = statusToneClasses('danger');
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-sm',
        errorTone.surfaceBorder,
        errorTone.surfaceBackground,
        errorTone.text,
        className
      )}
      role="alert"
    >
      {message}
    </div>
  );
}

export function FormErrorSummary({
  count,
  onJumpToFirstError,
  className,
}: {
  count: number;
  onJumpToFirstError?: () => void;
  className?: string;
}) {
  if (count <= 0) return null;
  const errorTone = statusToneClasses('danger');
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm',
        errorTone.surfaceBorder,
        errorTone.surfaceBackground,
        errorTone.text,
        className
      )}
    >
      <span>{count} campi da correggere</span>
      {onJumpToFirstError ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={errorTone.text}
          onClick={onJumpToFirstError}
        >
          Vai al primo errore
        </Button>
      ) : null}
    </div>
  );
}

export function EntityDialogFooter({
  primary,
  secondary,
  extra,
  className,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  extra?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex w-full flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between',
        className
      )}
    >
      <div className="flex items-center gap-2">{extra}</div>
      <div className="flex items-center gap-2">
        {secondary}
        {primary}
      </div>
    </div>
  );
}
